import { Confirm, ConfirmProps } from '@unistocks/components/confirm/confirm';
import { getComponentDisplayName } from '@unistocks/shared/utils/get-display-name';

type PickConfirmProps = Pick<
  ConfirmProps,
  | 'enabled'
  | 'type'
  | 'title'
  | 'message'
  | 'onConfirm'
  | 'onCancel'
  | 'cancelText'
  | 'confirmText'
  | 'requiredInputConfirmation'
  | 'requiredInputConfirmationMessage'
  | 'isLoading'
>;

/**
 * HOC that wraps a component with a confirm dialog.
 * @param Component The component to wrap
 * @returns A new component that shows a confirm dialog before executing the original component
 */
export function withConfirm<T extends object>(Component: React.ComponentType<T>) {
  const WrappedWithConfirm = ({
    enabled,
    type,
    title,
    message,
    onConfirm,
    onCancel,
    cancelText,
    confirmText,
    requiredInputConfirmation,
    requiredInputConfirmationMessage,
    isLoading,
    ...props
  }: T & PickConfirmProps) => (
    <Confirm
      enabled={enabled}
      type={type}
      title={title}
      message={message}
      onConfirm={onConfirm}
      onCancel={onCancel}
      cancelText={cancelText}
      confirmText={confirmText}
      requiredInputConfirmation={requiredInputConfirmation}
      requiredInputConfirmationMessage={requiredInputConfirmationMessage}
      isLoading={isLoading}
    >
      <Component {...(props as T)} />
    </Confirm>
  );

  WrappedWithConfirm.displayName = `withConfirm(${getComponentDisplayName(Component)})`;

  return WrappedWithConfirm;
}
